/**
 * Commercial service bucket a usage row is counted under (Phase 1 usage analytics).
 * Buckets are nested, not additive: a record's base bucket is its top service; every
 * estimate / upgrade round adds its own row with its own bucket.
 * @enum {string}
 */
export enum UsageServiceBucket {
  /** Pure visual capture — no AI, no human review, no estimate. */
  VISUAL = 'VISUAL',
  /** `EXPEDITED_AI` flow: quick AI condition report confirmed by an inspector, no estimate. */
  AI_ONLY = 'AI_ONLY',
  /** `VISUAL` flow with the `AIwH` service: AI condition report + human review. */
  AI_HUMAN = 'AI_HUMAN',
  /** `EST` service: condition report + monetary estimate (one row per estimate round). */
  ESTIMATE = 'ESTIMATE',
}

/**
 * Lifecycle stage of a usage row. Mirrors the hub's `inspectionLifecycleStages` verbatim.
 * @enum {string}
 */
export enum UsageStage {
  AWAITING = 'awaiting',
  CAPTURE = 'capture',
  REVIEW = 'review',
  COMPLETED = 'completed',
  CLOSED = 'closed',
}

/**
 * Where a usage row comes from.
 * @enum {string}
 */
export enum UsageRowSource {
  /** The inspection's own capture / base service. */
  BASE = 'BASE',
  /** One `inspection_service_requests` round of kind `ESTIMATE`. */
  ESTIMATE_ROUND = 'ESTIMATE_ROUND',
  /** One `inspection_service_requests` round of kind `UPGRADE`. */
  UPGRADE_ROUND = 'UPGRADE_ROUND',
}

/**
 * Which date series a record list / export is anchored to.
 * @enum {string}
 */
export enum UsageDateKind {
  /** Rows whose requested date falls in the period. */
  REQUESTED = 'requested',
  /** Rows whose completed date falls in the period. */
  COMPLETED = 'completed',
}

/**
 * Trend bucket granularity.
 * @enum {string}
 */
export enum UsageTrendBucket {
  DAY = 'day',
  WEEK = 'week',
  MONTH = 'month',
}

/**
 * The counted "record" of a vertical.
 * @enum {string}
 */
export enum UsageUnit {
  INSPECTION = 'inspection',
  CONTRACT = 'contract',
}

/**
 * Short, stable vertical keys accepted by the `verticals` filter.
 * @enum {string}
 */
export enum UsageVerticalKey {
  DEALER = 'dlr',
  LEASING = 'lsg',
  SELF_INSPECTION = 'slf',
  RENTAL = 'rnt',
  FLOORING = 'flr',
}

/**
 * Sortable columns of the usage records list.
 * @enum {string}
 */
export enum UsageRecordsSortField {
  requestedAt = 'requestedAt',
  completedAt = 'completedAt',
  inspectionId = 'inspectionId',
  service = 'service',
  stage = 'stage',
}

/**
 * Sortable columns of the usage by-location list.
 * @enum {string}
 */
export enum UsageByLocationSortField {
  requested = 'requested',
  completed = 'completed',
  completionRate = 'completionRate',
  inProgress = 'inProgress',
  cancelled = 'cancelled',
  locationId = 'locationId',
}

/**
 * Grouping of the usage by-location list: per location (default) or rolled up per company —
 * used by all-tenant principals before a company is picked.
 * @enum {string}
 */
export enum UsageLocationGroupBy {
  LOCATION = 'location',
  COMPANY = 'company',
}

/**
 * Date column the status series window and buckets apply to.
 * @enum {string}
 */
export enum OperationsDateKind {
  CREATED = 'created',
  COMPLETED = 'completed',
}

/**
 * Sortable columns of the operations inspectors list.
 * @enum {string}
 */
export enum OperationsInspectorSortField {
  total = 'total',
  completed = 'completed',
  completionRate = 'completionRate',
  captureMedianMinutes = 'captureMedianMinutes',
  turnaroundMedianMinutes = 'turnaroundMedianMinutes',
  withDamagesShare = 'withDamagesShare',
  redoRate = 'redoRate',
  inspectorId = 'inspectorId',
}

/**
 * Role of a rental contract's child inspection, read from the middle segment of its id
 * (`ins_pck_…` / `ins_drp_…` / `ins_emp_…`).
 * @enum {string}
 */
export enum RentalInspectionKind {
  PICK_UP = 'PICK_UP',
  DROP_OFF = 'DROP_OFF',
  EMPLOYEE = 'EMPLOYEE',
}

/**
 * Sortable columns of the rental contract records list.
 * @enum {string}
 */
export enum RentalRecordsSortField {
  requestedAt = 'requestedAt',
  completedAt = 'completedAt',
  contractNumber = 'contractNumber',
  stage = 'stage',
  pickUpDate = 'pickUpDate',
}
